import { Group, Menu, Text } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import AcceptAction from 'components/tables/data/revision/actions/AcceptAction';
import AssignToSelfAction from 'components/tables/data/revision/actions/AssignToSelfAction';
import AcceptDelegationAction from 'components/tables/data/revision/actions/AcceptDelegationAction';
import ApproveAction from 'components/tables/data/revision/actions/ApproveAction';
import ApproveAndConfirmAction from 'components/tables/data/revision/actions/ApproveAndConfirmAction';
import ConfirmAction from 'components/tables/data/revision/actions/confirm/ConfirmAction';
import CopyAction from 'components/tables/data/revision/actions/CopyAction';
import DeclineAction from 'components/tables/data/revision/actions/DeclineAction';
import DelegateAction from 'components/tables/data/revision/actions/DelegateAction';
import DeleteAction from 'components/tables/data/revision/actions/DeleteAction';
import EditAction from 'components/tables/data/revision/actions/EditAction';
import FillOutAction from 'components/tables/data/revision/actions/FillOutAction';
import FinishSigningAction from 'components/tables/data/revision/actions/FinishSigningAction';
import GeneratePdfAction from 'components/tables/data/revision/actions/GeneratePdfAction';
import GetSignedDocumentAgainAction from 'components/tables/data/revision/actions/GetSignedDocumentAgainAction';
import PrintPdfAction from 'components/tables/data/revision/actions/PrintPdfAction';
import ReassignAction from 'components/tables/data/revision/actions/ReassignAction';
import RejectAction from 'components/tables/data/revision/actions/RejectAction';
import RejectDelegationAction from 'components/tables/data/revision/actions/RejectDelegationAction';
import ReopenAction from 'components/tables/data/revision/actions/ReopenAction';
import ReturnAction from 'components/tables/data/revision/actions/ReturnAction';
import SignAction from 'components/tables/data/revision/actions/SignAction';
import SignAgainAction from 'components/tables/data/revision/actions/SignAgainAction';
import ViewAction from 'components/tables/data/revision/actions/ViewAction';
import {
  OnNoLongerAccessible,
  OnStatusChange,
  RevisionActionRevision,
  RevisionSingleActionProps,
} from 'components/tables/data/revision/types';
import {
  FEATURE_TOGGLE_KEP,
  FEATURE_TOGGLE_REVISION_ADMIN_GENERATE_PDF,
  FEATURE_TOGGLE_SAVE_SIGNED_DOC_AGAIN_ACTION,
} from 'env';
import panic from 'errors/panic';
import ActionIcon from 'components/ActionIcon';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Enum of all possible action names.
 */
type ActionName =
  | 'accept'
  | 'assign-to-self'
  | 'accept-delegation'
  | 'reject-delegation'
  | 'approve'
  | 'confirm'
  | 'copy'
  | 'decline'
  | 'delete'
  | 'edit'
  | 'fill-out'
  | 'reassign'
  | 'reject'
  | 'reopen'
  | 'return'
  | 'approve-confirm'
  | 'view'
  | 'sign-with-kep'
  | 'sign-again-with-kep'
  | 'finish-signing-with-kep'
  | 'get-signed-document-again'
  | 'delegate'
  | 'generate-pdf'
  | 'print-pdf';

/**
 * Discriminates between different actions.
 */
export function ActionDiscriminator({ actionName, ...props }: { actionName: ActionName } & RevisionSingleActionProps) {
  if (actionName === 'accept') {
    return <AcceptAction {...props} />;
  }

  if (actionName === 'assign-to-self') {
    return <AssignToSelfAction {...props} />;
  }

  if (actionName === 'accept-delegation') {
    return <AcceptDelegationAction {...props} />;
  }

  if (actionName === 'reject-delegation') {
    return <RejectDelegationAction {...props} />;
  }

  if (actionName === 'edit') {
    return <EditAction {...props} />;
  }

  if (actionName === 'view') {
    return <ViewAction {...props} />;
  }

  if (actionName === 'fill-out') {
    return <FillOutAction {...props} />;
  }

  if (actionName === 'delegate') {
    return <DelegateAction {...props} />;
  }

  if (actionName === 'approve') {
    return <ApproveAction {...props} />;
  }

  if (actionName === 'confirm') {
    return <ConfirmAction {...props} />;
  }

  if (actionName === 'reopen') {
    return <ReopenAction {...props} />;
  }

  if (actionName === 'decline') {
    return <DeclineAction {...props} />;
  }

  if (actionName === 'reject') {
    return <RejectAction {...props} />;
  }

  if (actionName === 'return') {
    return <ReturnAction {...props} />;
  }

  if (actionName === 'reassign') {
    return <ReassignAction {...props} />;
  }

  if (actionName === 'copy') {
    return <CopyAction {...props} />;
  }

  if (actionName === 'delete') {
    return <DeleteAction {...props} />;
  }

  if (actionName === 'approve-confirm') {
    return <ApproveAndConfirmAction {...props} />;
  }

  if (FEATURE_TOGGLE_KEP && actionName === 'sign-with-kep') {
    return <SignAction {...props} />;
  }

  if (FEATURE_TOGGLE_KEP && actionName === 'sign-again-with-kep') {
    return <SignAgainAction {...props} />;
  }

  if (FEATURE_TOGGLE_KEP && actionName === 'finish-signing-with-kep') {
    return <FinishSigningAction {...props} />;
  }

  if (FEATURE_TOGGLE_KEP && actionName === 'get-signed-document-again') {
    return <GetSignedDocumentAgainAction {...props} />;
  }

  if (FEATURE_TOGGLE_REVISION_ADMIN_GENERATE_PDF && actionName === 'generate-pdf') {
    return <GeneratePdfAction {...props} />;
  }

  if (actionName === 'print-pdf') {
    return <PrintPdfAction {...props} />;
  }

  return <></>;
}

/**
 * Parameters of the RevisionActions component.
 */
export interface RevisionActionsProps {
  revision: RevisionActionRevision;
  hideView?: boolean;
  viewOnly?: boolean;
  compactOnTablet?: boolean;
  keepPrimaryActionOnMobile?: boolean;
  onStatusChange?: OnStatusChange;
  onNoLongerAccessible?: OnNoLongerAccessible;
}

/**
 * Displays actions for a revision in a table
 */
export default function RevisionActions({
  revision: incomingRevision,
  hideView = false,
  viewOnly = false,
  compactOnTablet = false,
  keepPrimaryActionOnMobile = false,
  onStatusChange = noop,
  onNoLongerAccessible = noop,
}: RevisionActionsProps) {
  const { userId, getAction, roleId } = useApi();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile' && !keepPrimaryActionOnMobile;
  const [revision, setRevision] = useState<RevisionActionRevision>(incomingRevision);

  useEffect(() => {
    setRevision(incomingRevision);
  }, [incomingRevision]);

  const refreshRevision = useCallback(async () => {
    const getRevision = getAction('RevisionGet');
    const refreshedRevision = await getRevision({ parameters: { revisionId: String(revision.revisionId) } });

    setRevision(refreshedRevision);
  }, [getAction, revision.revisionId]);

  const canAssignRevisions = roleId === ROLE_ADMIN_ID || revision.actionPermissions.canAssign;
  const canApproveRevisions = roleId === ROLE_ADMIN_ID || revision.actionPermissions.canApprove;
  const canAcceptRevisions = roleId === ROLE_ADMIN_ID || revision.actionPermissions.canAccept;
  const canGeneratePdf = roleId === ROLE_ADMIN_ID || revision.actionPermissions.canGeneratePdf;

  const myDelegation = useMemo(() => revision?.delegatedTo?.find((user) => user.userId === userId), [revision, userId]);

  const isDelegationAccepted = useMemo(() => myDelegation?.state === 'accepted', [myDelegation]);

  const isDelegationPending = useMemo(() => myDelegation?.state === 'pending', [myDelegation]);

  const actions = useMemo(() => {
    if (!revision) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return [];
    }

    const { slug } = revision.revisionStatus;
    const { userId: technicianId = null } = revision.technician || {};
    const actions: ActionName[] = [];
    const hasRevisionData = revision.hasRevisionData;
    const hasActiveRevisionTemplate = !!revision.revisionTemplate?.status;
    const hasRenderableRevisionTemplate = revision.revisionTemplate?.isRenderable === true;

    if ((slug === 'created' || slug === 'in-progress') && !technicianId && revision.actionPermissions.canAssignToSelf) {
      actions.push('assign-to-self');
    }

    if (slug === 'created' && userId === technicianId) {
      // 1 -> 2, 1 -> 3
      actions.push('accept', 'decline');
    }

    // If the user has a pending delegation, they need to accept or reject it first
    if (isDelegationPending && (slug === 'accepted-by-technician' || slug === 'in-progress')) {
      actions.push('accept-delegation', 'reject-delegation');
    }

    if (
      (slug === 'accepted-by-technician' || slug === 'in-progress') &&
      (userId === technicianId || (isDelegationAccepted && !!revision.revisionTemplate))
    ) {
      // 2 -> 4, 4 -> 4
      actions.push('fill-out');
    }

    // finish revision is available only from the form

    if (slug === 'finished-waiting-for-approval' && canApproveRevisions) {
      if (canAcceptRevisions) {
        // 5 -> 8
        // actions.push('approve-confirm');
      }

      // 5 -> 6
      actions.push('approve', 'reject');
    }

    if (slug === 'rejected' && canAssignRevisions) {
      // 6 -> 4
      actions.push('reopen');
    }

    if (slug === 'approved-waiting-for-confirmation' && canAcceptRevisions) {
      // 7 -> 8, 7 -> 6
      actions.push('confirm', 'return');
    }

    if (FEATURE_TOGGLE_KEP && revision.kepSignable && slug === 'confirmed' && userId === technicianId) {
      // 8 -> 9
      actions.push('sign-with-kep');
    }

    if (FEATURE_TOGGLE_KEP && revision.kepSignable && slug === 'being-signed' && userId === technicianId) {
      // 9 -> 10
      actions.push('finish-signing-with-kep');

      // re-send the revision to the signing portal
      actions.push('sign-again-with-kep');
    }

    if (
      slug !== 'confirmed' &&
      canAssignRevisions &&
      slug !== 'being-signed' &&
      slug !== 'signed-with-kep' &&
      slug !== 'deleted'
    ) {
      actions.push('edit');
    }

    if (slug === 'declined-by-technician' && canAssignRevisions) {
      // 3 -> 1
      actions.push('reassign');
    }

    if (!hideView) {
      actions.push('view');
    }

    if (slug === 'in-progress' && revision.revisionTemplate && userId === technicianId) {
      actions.push('delegate');
    }

    if (canAssignRevisions && (!hasRevisionData || hasActiveRevisionTemplate) && !!technicianId) {
      actions.push('copy');
    }

    if (
      FEATURE_TOGGLE_REVISION_ADMIN_GENERATE_PDF &&
      canGeneratePdf &&
      revision.hasRevisionData &&
      hasRenderableRevisionTemplate
    ) {
      actions.push('generate-pdf');
    }

    if (revision.fileId) {
      actions.push('print-pdf');
    }

    if (
      FEATURE_TOGGLE_KEP &&
      FEATURE_TOGGLE_SAVE_SIGNED_DOC_AGAIN_ACTION &&
      revision.kepSignable &&
      slug === 'signed-with-kep' &&
      revision.actionPermissions.canDownloadArchiveClause
    ) {
      // fetch the archive clause again when the backend grants access
      actions.push('get-signed-document-again');
    }

    if (
      (slug === 'created' ||
        slug === 'declined-by-technician' ||
        slug === 'accepted-by-technician' ||
        slug === 'in-progress') &&
      canAssignRevisions
    ) {
      actions.push('delete');
    }

    return actions;
  }, [
    revision,
    userId,
    roleId,
    canAssignRevisions,
    canApproveRevisions,
    canAcceptRevisions,
    canGeneratePdf,
    isDelegationAccepted,
    isDelegationPending,
  ]);

  const primaryAction = useMemo(() => {
    if (menuOnly) {
      return null;
    }

    return viewOnly ? 'view' : actions[0];
  }, [actions, menuOnly, viewOnly]);
  const menuActions = useMemo(() => {
    if (menuOnly) {
      return actions;
    }

    return viewOnly ? [] : actions.slice(1);
  }, [actions, menuOnly, viewOnly]);

  const handleStatusChange: OnStatusChange = useCallback(
    (params) => {
      onStatusChange(params);

      setRevision((currentRevision) => {
        if (currentRevision.revisionId !== params.revisionId) {
          return currentRevision;
        }

        if (!params.technician) {
          return {
            ...currentRevision,
            revisionStatus: params.status,
            fileId: params.fileId ?? currentRevision.fileId,
          };
        }

        return {
          ...currentRevision,
          revisionStatus: params.status,
          fileId: params.fileId ?? currentRevision.fileId,
          technician: params.technician,
          actionPermissions: { ...currentRevision.actionPermissions, canAssignToSelf: false },
        };
      });

      if (params.status.revisionStatusId === revision.revisionStatus.revisionStatusId) {
        refreshRevision().catch((error) => panic(error as any));
      }
    },
    [onStatusChange, refreshRevision, revision.revisionStatus.revisionStatusId]
  );

  return (
    <Group noWrap spacing={12}>
      {primaryAction && (
        <ActionDiscriminator
          actionName={primaryAction}
          revision={revision}
          onStatusChange={handleStatusChange}
          onNoLongerAccessible={onNoLongerAccessible}
          kind={isCompact ? 'action-icon' : 'button'}
        />
      )}
      {menuActions.length > 0 && (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
              <IconDotsVertical stroke="1.5" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {revision.revisionName}
              </Text>
            </Menu.Label>

            {menuActions.map((action) => (
              <ActionDiscriminator
                key={action}
                actionName={action}
                revision={revision}
                onStatusChange={handleStatusChange}
                onNoLongerAccessible={onNoLongerAccessible}
                kind="menu"
              />
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}
